import React, { useCallback, useState } from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import Popup, { type TriggerProps } from '@atlaskit/popup';
import { Box } from '@atlaskit/primitives/compiled';
import TextField from '@atlaskit/textfield';

// eslint-disable-next-line @atlassian/tangerine/import/no-restricted-paths
import { useAnalytics, useCommonAndPrivateAttributes } from '../../../../../controllers/analytics';
import { type ProductKeys } from '../../../../constants/products';
import { type ThirdPartyConfigsBootstrap } from '../../../../constants/schemas/3p-config';
import type { FireAnalyticsEvent, TriggerComponentProps } from '../../../../types';
import { isProductSmartlink } from '../../../../utils/connectors';
import { isProductConnected } from '../../../../utils/connectors';
import type { OAuthSource } from '../../../../utils/events/oauth-events';
import { onAppFilterOptionClicked } from '../../../../utils/events/product-option-clicked';
import {
	onAppFilterShowMoreTextFieldFocused,
	onAppFilterShowMoreClicked,
	onAppFilterShowMoreHovered,
	onAppFilterOptionHovered,
} from '../../../../utils/events/right-panel-app-filter-events';
// eslint-disable-next-line @atlassian/tangerine/import/no-restricted-paths
import { getUnifiedSearchRightRailEnhancementsExpValue } from '../../../../utils/experiments';
import { useDebouncedCallback } from '../../../../utils/use-debounced-callback';
import {
	OAuthWarningWrapper,
	type UseIsUserOAuthed,
	type UseOAuthHandlers,
} from '../../../oauth-warning-wrapper';
import FilterOption from '../filter-option';

import { messages } from './messages';

export interface FilterOption {
	label: React.ReactNode;
	count?: number;
	value: ProductKeys | undefined;
	isSelected: boolean;
	onSelect: () => void;
	hideFilter: () => void;
	showFilter: () => void;
	compositeKey?: string;
	datasourceId?: string;
}

type ShowMoreAppsDialogParams = {
	options: FilterOption[];
	useIsUserOAuthed: UseIsUserOAuthed;
	useOAuthHandlers: UseOAuthHandlers;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	oAuthSource: OAuthSource;
	trigger?: (triggerProps: TriggerProps & { onClick: () => void }) => React.ReactNode;
	shouldFitContainer?: boolean;
	offset?: [number, number] | undefined;
	isConnected?: boolean;
	onTriggerClick?: () => void;
};

const styles = cssMap({
	showMoreApps: {
		/**
		 * Show more apps popup max height calculation:
		 * 	top padding = 8px
		 * 	text field height = 40px
		 * 	text field bottom padding = 12px
		 * 	max visible options = 8
		 * 	option height = 38px
		 * total = 8px + 40px + 12px + (8 * 38px) = 364px
		 */
		maxHeight: '364px',
		overflowY: 'auto',
	},
});

type ShowMorePopupItemProps = {
	option: FilterOption;
	index: number;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	useIsUserOAuthed: UseIsUserOAuthed;
	useOAuthHandlers: UseOAuthHandlers;
	oAuthSource: OAuthSource;
	setIsOpen: (open: boolean) => void;
	fireAnalyticsEvent: (event: FireAnalyticsEvent) => void;
	commonAttributes: ReturnType<typeof useCommonAndPrivateAttributes>['commonAttributes'];
	nonPrivacySafeAttributes: ReturnType<
		typeof useCommonAndPrivateAttributes
	>['nonPrivacySafeAttributes'];
};

const ShowMorePopupItem = ({
	option,
	index,
	thirdPartyConfigs,
	useIsUserOAuthed,
	useOAuthHandlers,
	oAuthSource,
	setIsOpen,
	fireAnalyticsEvent,
	commonAttributes,
	nonPrivacySafeAttributes,
}: ShowMorePopupItemProps): React.JSX.Element => {
	const isSmartlink = isProductSmartlink({ productKey: option.value, thirdPartyConfigs });
	const reactKey = option.compositeKey || option.value;
	const itemIsConnected = isProductConnected(option.value, thirdPartyConfigs);

	const debouncedOnHover = useDebouncedCallback(
		useCallback(() => {
			fireAnalyticsEvent(
				onAppFilterOptionHovered({
					commonAttributes,
					nonPrivacySafeAttributes,
					productKey: option.value as ProductKeys,
					isConnected: itemIsConnected,
					index,
					isInShowMore: true,
				}),
			);
		}, [
			fireAnalyticsEvent,
			commonAttributes,
			nonPrivacySafeAttributes,
			option.value,
			itemIsConnected,
			index,
		]),
		500,
	);

	return (
		<OAuthWarningWrapper
			datasourceId={option.datasourceId}
			key={reactKey}
			product={option.value as ProductKeys}
			trigger={({ oAuthWarning, triggerProps }: TriggerComponentProps) => (
				<FilterOption
					oAuthWarning={oAuthWarning}
					triggerProps={triggerProps}
					key={reactKey}
					isHidden={true}
					option={option}
					onClick={() => {
						if (!oAuthWarning) {
							fireAnalyticsEvent(
								onAppFilterOptionClicked({
									commonAttributes,
									nonPrivacySafeAttributes,
									productKey: option.value as ProductKeys,
									isConnected: itemIsConnected,
									index,
									isInShowMore: true,
								}),
							);
							option.showFilter();
							option.onSelect();
							setIsOpen(false);
						}
					}}
					onHover={debouncedOnHover}
					ariaRole="listitem"
				/>
			)}
			useOAuthHandlers={useOAuthHandlers}
			useIsUserOAuthed={useIsUserOAuthed}
			option={option}
			isSmartlink={isSmartlink}
			oAuthSource={oAuthSource}
			thirdPartyConfigs={thirdPartyConfigs}
			postAuthAction={
				getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select'
					? () => {
							setIsOpen(false);
						}
					: undefined
			}
			skipFlagsProvider={getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select'}
		/>
	);
};

const ShowMoreAppsDialog = ({
	options = [],
	useIsUserOAuthed,
	useOAuthHandlers,
	thirdPartyConfigs,
	oAuthSource,
	trigger,
	shouldFitContainer,
	offset,
	isConnected = true,
	onTriggerClick,
}: ShowMoreAppsDialogParams): React.JSX.Element | null => {
	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useCommonAndPrivateAttributes();
	const [isOpen, setIsOpen] = useState(false);
	const [filter, setFilter] = useState<RegExp>();
	const { formatMessage } = useIntl();

	const debouncedOnShowMoreHover = useDebouncedCallback(
		useCallback(() => {
			fireAnalyticsEvent(
				onAppFilterShowMoreHovered({
					commonAttributes,
					nonPrivacySafeAttributes,
					isConnected,
				}),
			);
		}, [fireAnalyticsEvent, commonAttributes, nonPrivacySafeAttributes, isConnected]),
		500,
	);

	if (!options.length) {
		return null;
	}

	return (
		<Popup
			content={() => (
				<Box paddingBlock="space.100" xcss={styles.showMoreApps}>
					<Box paddingInline="space.100" paddingBlockEnd="space.150">
						<TextField
							placeholder={formatMessage(messages.filterPlaceholder)}
							onChange={(ev) => setFilter(new RegExp(ev.currentTarget.value, 'i'))}
							onFocus={() => {
								fireAnalyticsEvent(
									onAppFilterShowMoreTextFieldFocused({
										commonAttributes,
										nonPrivacySafeAttributes,
									}),
								);
							}}
						/>
					</Box>
					<Box role="list">
						{options
							.filter(
								(option) =>
									!filter || (typeof option.label === 'string' && filter.test(option.label)),
							)
							.map((option, index) => (
								<ShowMorePopupItem
									key={option.compositeKey || option.value}
									option={option}
									index={index}
									thirdPartyConfigs={thirdPartyConfigs}
									useIsUserOAuthed={useIsUserOAuthed}
									useOAuthHandlers={useOAuthHandlers}
									oAuthSource={oAuthSource}
									setIsOpen={setIsOpen}
									fireAnalyticsEvent={fireAnalyticsEvent}
									commonAttributes={commonAttributes}
									nonPrivacySafeAttributes={nonPrivacySafeAttributes}
								/>
							))}
					</Box>
				</Box>
			)}
			isOpen={isOpen}
			onClose={() => {
				setIsOpen(false);
				setFilter(undefined);
			}}
			placement="bottom-start"
			shouldFitContainer={shouldFitContainer}
			offset={offset}
			trigger={(triggerProps) =>
				trigger ? (
					trigger({
						...triggerProps,
						onClick: () => {
							onTriggerClick?.();
							setIsOpen((prev) => !prev);
							setFilter(undefined);
						},
					})
				) : (
					<FilterOption
						oAuthWarning={null}
						triggerProps={triggerProps}
						isDisabled={false}
						isShowMore={true}
						option={{
							label: formatMessage(
								getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select'
									? messages.showAllAppsLabel
									: messages.showMoreLabel,
							),
							isSelected: false,
							value: undefined,
						}}
						onClick={() => {
							onTriggerClick?.();
							setIsOpen(true);
							setFilter(undefined);
							fireAnalyticsEvent(
								onAppFilterShowMoreClicked({
									commonAttributes,
									nonPrivacySafeAttributes,
									isConnected,
									section: isConnected ? 'filterResults' : 'connectApps',
								}),
							);
						}}
						onHover={debouncedOnShowMoreHover}
					/>
				)
			}
			modifiers={[
				{
					name: 'computeStyles',
					options: {
						// Prevent use of 3D transforms to avoid creating a new stacking context for fixed position children
						// This allows the connection prompt popup to appear to the left of this dialog
						gpuAcceleration: false,
					},
				},
			]}
		/>
	);
};

export default ShowMoreAppsDialog;
